import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { matchQuery, QueryKey, useQueryClient } from '@tanstack/react-query'
import { RefObject, useEffect } from 'react'

/**
 * Re-syncs a grid's `unstable_dataSource` whenever a matching query is invalidated or GC'd.
 * `queryKey: undefined` no-ops — call unconditionally per the rules of hooks.
 * `resync` replaces the default full `fetchRows()`; it must be referentially stable.
 */
export const useSyncDataSourceOnInvalidate = (
  apiRef: RefObject<GridApiPremium | null>,
  queryKey: QueryKey | undefined,
  resync?: () => void
) => {
  const queryClient = useQueryClient()

  // a query key declaration in the parent component body would reference a different array object instance, and
  // would trigger the useEffect each render. serialising to prevent this.
  const queryKeyDependency = JSON.stringify(queryKey)

  useEffect(() => {
    if (!queryKey) return

    let queued = false
    const queueFetchRows = () => {
      if (queued) return
      queued = true
      // Coalesces multiple queries invalidated in one tick into a single fetchRows() call.
      queueMicrotask(() => {
        queued = false
        if (resync) resync()
        else apiRef.current?.unstable_dataSource?.fetchRows()
      })
    }

    // Tracks queries invalidated mid-fetch — that fetch resolves with pre-mutation data, so wait for
    // it to settle before refetching.
    const midFlightInvalidations = new Set<string>()

    // Refetch only once no hash remains — any survivor can catch a future invalidate, since
    // fetchRows() always refetches the current view regardless of which query fired.
    const liveQueryHashes = new Set<string>()

    return queryClient.getQueryCache().subscribe((event) => {
      if (!matchQuery({ queryKey }, event.query)) return

      if (event.type === 'removed') {
        // No observers keep this alive, so gcTime drops it eventually — recreate one if none remain.
        liveQueryHashes.delete(event.query.queryHash)
        if (liveQueryHashes.size === 0) queueFetchRows()
        return
      }

      liveQueryHashes.add(event.query.queryHash)

      if (event.type !== 'updated') return

      if (event.action.type === 'invalidate') {
        if (event.query.state.fetchStatus === 'idle') {
          queueFetchRows()
        } else {
          midFlightInvalidations.add(event.query.queryHash)
        }
        return
      }

      const settledAfterMidFlightInvalidate =
        (event.action.type === 'success' || event.action.type === 'error') && midFlightInvalidations.delete(event.query.queryHash)
      if (!settledAfterMidFlightInvalidate) return

      if (event.action.type === 'success') {
        // Success clears `isInvalidated`, so it must be reinstated or fetchRows() below serves the
        // same stale result; error never clears it.
        void queryClient.invalidateQueries({ queryKey: event.query.queryKey, exact: true })
      }
      queueFetchRows()
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- depend on the stable serialization, not `queryKey`'s identity
  }, [queryClient, apiRef, queryKeyDependency, resync])
}
